import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth, API, authFetch } from "@/App";
import { toast } from "sonner";

import { 
  ArrowLeft, Clock, Save, TrendingUp, TrendingDown, Minus,
  Briefcase, Moon, Dumbbell, Users, BookOpen, Palette, 
  UtensilsCrossed, Car, ShoppingCart, Timer, AlertCircle, 
  CheckCircle, Loader2, BarChart3, Calendar
} from "lucide-react";

const CATEGORIES = [
  { id: "work_hours", name: "Work", icon: Briefcase, color: "bg-blue-500" },
  { id: "sleep_hours", name: "Sleep", icon: Moon, color: "bg-purple-500" },
  { id: "exercise_hours", name: "Exercise", icon: Dumbbell, color: "bg-green-500" },
  { id: "family_time_hours", name: "Family/Social", icon: Users, color: "bg-pink-500" },
  { id: "personal_development_hours", name: "Personal Dev", icon: BookOpen, color: "bg-amber-500" },
  { id: "hobbies_hours", name: "Hobbies", icon: Palette, color: "bg-cyan-500" },
  { id: "meals_self_care_hours", name: "Meals & Self-Care", icon: UtensilsCrossed, color: "bg-orange-500" },
  { id: "commute_hours", name: "Commute", icon: Car, color: "bg-gray-500" },
  { id: "errands_chores_hours", name: "Errands & Chores", icon: ShoppingCart, color: "bg-rose-500" },
  { id: "buffer_hours", name: "Buffer Time", icon: Timer, color: "bg-neutral-500" }
];

const getWeekStart = (date = new Date()) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  return d.toISOString().split('T')[0];
};

export const TimeBudgetActualsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentWeek, setCurrentWeek] = useState(getWeekStart());
  const [actuals, setActuals] = useState({});
  const [comparison, setComparison] = useState(null);
  const [recentWeeks, setRecentWeeks] = useState([]);

  useEffect(() => {
    fetchData();
  }, [currentWeek]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [actualsRes, comparisonRes, historyRes] = await Promise.all([
        authFetch(`${API}/time-budget/actuals/${currentWeek}`, {  }),
        authFetch(`${API}/time-budget/comparison?week_start=${currentWeek}`, {  }),
        authFetch(`${API}/time-budget/actuals?weeks=8`, {  })
      ]);

      if (actualsRes.ok) {
        const data = await actualsRes.json();
        setActuals(data || {});
      }
      if (comparisonRes.ok) {
        setComparison(await comparisonRes.json());
      }
      if (historyRes.ok) {
        setRecentWeeks(await historyRes.json());
      }
    } catch (_error) {
      console.error("Error fetching data:", _error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await authFetch(`${API}/time-budget/actuals`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({
          week_start: currentWeek,
          ...actuals
        })
      });

      if (response.ok) {
        toast.success("Actuals saved!");
        fetchData();
      } else {
        toast.error("Failed to save actuals");
      }
    } catch (_error) {
      toast.error("An error occurred");
    } finally {
      setSaving(false);
    }
  };

  const updateActual = (categoryId, value) => {
    setActuals(prev => ({
      ...prev,
      [categoryId]: parseFloat(value) || 0
    }));
  };

  const getVarianceIcon = (status) => {
    if (status === "over") return <TrendingUp className="w-4 h-4 text-red-500" />;
    if (status === "under") return <TrendingDown className="w-4 h-4 text-amber-500" />;
    return <Minus className="w-4 h-4 text-green-500" />;
  };

  const getVarianceColor = (status) => {
    if (status === "over") return "text-red-600 bg-red-50";
    if (status === "under") return "text-amber-600 bg-amber-50";
    return "text-green-600 bg-green-50";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="w-8 h-8 border-2 border-[#333333] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="time-actuals-page">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/time-budget")} className="text-neutral-600">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Time Budget
              </Button>
              <div>
                <h1 className="text-xl font-semibold text-[#333333]">Track Actual Hours</h1>
                <p className="text-sm text-neutral-500">Compare your planned vs. actual time</p>
              </div>
            </div>
            <Button onClick={handleSave} disabled={saving} className="bg-[#333333] hover:bg-[#444444] text-white">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Actuals
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Week Selector */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Calendar className="w-5 h-5 text-[#39FF14]" />
                <div>
                  <Label htmlFor="week-select">Week Starting</Label>
                  <Input
                    id="week-select"
                    type="date"
                    value={currentWeek}
                    onChange={(e) => setCurrentWeek(e.target.value)}
                    className="w-40"
                  />
                </div>
              </div>
              <div className="flex space-x-2">
                {recentWeeks.slice(0, 4).map((week) => (
                  <Button
                    key={week.week_start}
                    variant={currentWeek === week.week_start ? "default" : "outline"}
                    size="sm"
                    onClick={() => setCurrentWeek(week.week_start)}
                    className={currentWeek === week.week_start ? "bg-[#333333]" : ""}
                  >
                    {new Date(week.week_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="log" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="log">Log Hours</TabsTrigger>
            <TabsTrigger value="compare">Compare</TabsTrigger>
          </TabsList>

          {/* Log Hours Tab */}
          <TabsContent value="log">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Clock className="w-5 h-5 mr-2 text-[#39FF14]" />
                  Actual Hours for Week of {new Date(currentWeek).toLocaleDateString()}
                </CardTitle>
                <CardDescription>Enter how many hours you actually spent in each category</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  {CATEGORIES.map((category) => {
                    const Icon = category.icon;
                    const actual = actuals[category.id] || 0;
                    
                    return (
                      <div key={category.id} className="flex items-center space-x-3 p-3 bg-neutral-50 rounded-lg">
                        <div className={`w-10 h-10 ${category.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
                          <Icon className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1">
                          <Label className="text-sm font-medium">{category.name}</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Input
                            type="number"
                            value={actual}
                            onChange={(e) => updateActual(category.id, e.target.value)}
                            className="w-20 text-right"
                            step="0.5"
                            min="0"
                            max="168"
                          />
                          <span className="text-sm text-neutral-500 w-6">hrs</span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Total */}
                <div className="mt-6 p-4 bg-neutral-100 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Total Logged</span>
                    <span className="text-xl font-bold">
                      {Object.values(actuals).reduce((sum, val) => sum + (parseFloat(val) || 0), 0).toFixed(1)} / 168 hours
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Compare Tab */}
          <TabsContent value="compare">
            {comparison && (
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid md:grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="p-4 text-center">
                      <p className="text-sm text-neutral-500">Budgeted</p>
                      <p className="text-2xl font-bold text-blue-600">{comparison.totals.budgeted}h</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <p className="text-sm text-neutral-500">Actual</p>
                      <p className="text-2xl font-bold text-[#39FF14]">{comparison.totals.actual}h</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <p className="text-sm text-neutral-500">Variance</p>
                      <p className={`text-2xl font-bold ${comparison.totals.variance > 0 ? 'text-red-600' : comparison.totals.variance < 0 ? 'text-amber-600' : 'text-green-600'}`}>
                        {comparison.totals.variance > 0 ? '+' : ''}{comparison.totals.variance}h
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Insights */}
                {comparison.insights && comparison.insights.length > 0 && (
                  <Card className="border-blue-200 bg-blue-50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center">
                        <BarChart3 className="w-5 h-5 mr-2 text-blue-600" />
                        Insights
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {comparison.insights.map((insight, idx) => (
                          <div key={idx} className={`p-3 rounded-lg ${
                            insight.type === 'warning' ? 'bg-amber-100 text-amber-800' :
                            insight.type === 'suggestion' ? 'bg-blue-100 text-blue-800' :
                            'bg-neutral-100 text-neutral-800'
                          }`}>
                            <p className="text-sm">{insight.message}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Detailed Comparison */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Category Breakdown</CardTitle>
                    <CardDescription>Budget vs. Actual comparison by category</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {comparison.comparison.map((item) => {
                        const category = CATEGORIES.find(c => c.id === item.category_id);
                        const Icon = category?.icon || Clock;
                        
                        return (
                          <div key={item.category_id} className="flex items-center space-x-4 p-3 bg-neutral-50 rounded-lg">
                            <div className={`w-10 h-10 ${category?.color || 'bg-gray-500'} rounded-lg flex items-center justify-center flex-shrink-0`}>
                              <Icon className="w-5 h-5 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-neutral-900">{item.category}</p>
                              <div className="flex items-center space-x-4 text-sm text-neutral-500">
                                <span>Budget: {item.budgeted}h</span>
                                <span>Actual: {item.actual}h</span>
                              </div>
                            </div>
                            <div className={`flex items-center space-x-2 px-3 py-1 rounded-full ${getVarianceColor(item.status)}`}>
                              {getVarianceIcon(item.status)}
                              <span className="text-sm font-medium">
                                {item.variance > 0 ? '+' : ''}{item.variance}h
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {!comparison.has_actuals && (
                  <Card className="border-amber-200 bg-amber-50">
                    <CardContent className="p-4">
                      <div className="flex items-center space-x-3">
                        <AlertCircle className="w-5 h-5 text-amber-600" />
                        <p className="text-amber-800">
                          No actuals logged for this week yet. Switch to "Log Hours" tab to enter your actual time.
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default TimeBudgetActualsPage;
